import { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ordersAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  Download, 
  FileText, 
  ArrowRight,
  LayoutDashboard,
  Clock
} from 'lucide-react';

export default function CheckoutConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  
  const order = location.state?.order;

  useEffect(() => {
    if (!order) {
      navigate('/services');
    }
  }, [order, navigate]);

  const formatCurrencyUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleDownloadReceipt = () => {
    window.open(`${ordersAPI.getReceipt(order.id)}?token=${token}`, '_blank');
  };

  const handleDownloadAgreement = () => {
    window.open(`${ordersAPI.getAgreement(order.id)}?token=${token}`, '_blank');
  };

  if (!order) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="confirmation-page">
      {/* Success Banner */}
      <div className="bg-[#D2E4D6] py-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="success-checkmark mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#056839] mb-2">Request Submitted!</h1>
          <p className="text-[#3F3F3F]">
            Thank you for your order request. Our team will contact you shortly to confirm your order and guide you through the payment process.
          </p>
        </div>
      </div>

      {/* Order Details */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-8 animate-fade-in">
          {/* Status Notice */}
          <div className="mb-6 p-4 bg-[#E4D5B7]/30 rounded-lg border border-[#E4D5B7]">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-[#A0522D]" />
              <div>
                <p className="font-semibold text-[#A0522D]">Awaiting review by AfriCycle Zambia</p>
                <p className="text-sm text-[#3F3F3F]">Your order is awaiting review and confirmation from our team.</p>
              </div>
            </div>
          </div>

          {/* Order Info */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-gray-200">
            <div>
              <p className="text-[#6B7280] text-sm">Order Number</p>
              <p className="text-xl font-semibold text-[#056839]">{order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-[#6B7280] text-sm">Order Date</p>
              <p className="text-[#3F3F3F] font-medium">{formatDate(order.created_at)}</p>
            </div>
          </div>

          {/* Items */}
          <div className="py-6 border-b border-gray-200">
            <h3 className="font-semibold text-[#056839] mb-4">Order Items</h3>
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2">
                <div>
                  <p className="text-[#3F3F3F] font-medium">{item.service_name}</p>
                  <p className="text-[#6B7280] text-sm">Qty: {item.quantity}</p>
                </div>
                <p className="font-semibold text-[#056839]">{formatCurrencyUSD(item.total_price)}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="py-6 border-b border-gray-200 space-y-2">
            <div className="flex justify-between">
              <span className="text-[#6B7280]">Subtotal</span>
              <span className="text-[#3F3F3F]">{formatCurrencyUSD(order.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#6B7280]">VAT (16%)</span>
              <span className="text-[#3F3F3F]">{formatCurrencyUSD(order.tax)}</span>
            </div>
            <div className="flex justify-between pt-2">
              <span className="font-semibold text-[#056839]">Total</span>
              <span className="text-2xl font-bold text-[#056839]">{formatCurrencyUSD(order.total)}</span>
            </div>
          </div>

          {/* Company Details */}
          <div className="py-6 border-b border-gray-200">
            <h3 className="font-semibold text-[#056839] mb-4">Company Details</h3>
            <div className="text-[#3F3F3F] space-y-1">
              <p className="font-medium">{order.company_details.company_name}</p>
              <p>{order.company_details.address}</p>
              <p>{order.company_details.city}, {order.company_details.country}</p>
              <p className="text-[#6B7280] text-sm">Reg: {order.company_details.registration_number}</p>
            </div>
          </div>

          {/* Download Documents */}
          <div className="py-6">
            <h3 className="font-semibold text-[#056839] mb-4">Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 justify-start"
                onClick={handleDownloadReceipt}
                data-testid="download-receipt-btn"
              >
                <Download className="h-5 w-5 mr-3 text-[#056839]" />
                <div className="text-left">
                  <p className="font-medium">Order Request Receipt</p>
                  <p className="text-xs text-[#6B7280]">PDF Document</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 justify-start"
                onClick={handleDownloadAgreement}
                data-testid="download-agreement-btn"
              >
                <FileText className="h-5 w-5 mr-3 text-[#056839]" />
                <div className="text-left">
                  <p className="font-medium">Participation Agreement</p>
                  <p className="text-xs text-[#6B7280]">PDF Document</p>
                </div>
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <Link to="/app" className="flex-1">
              <Button className="w-full btn-primary" data-testid="go-to-dashboard-btn">
                <LayoutDashboard className="mr-2 h-5 w-5" />
                Go to Dashboard
              </Button>
            </Link>
            <Link to="/services" className="flex-1">
              <Button variant="outline" className="w-full btn-outline" data-testid="browse-services-btn">
                Browse More Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center mt-8">
          <p className="text-[#6B7280] text-sm">
            Need help? Contact us at{' '}
            <a href="mailto:africycle@africyclezambia.pro" className="text-[#056839] hover:underline">
              africycle@africyclezambia.pro
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
